<template>
  <v-dialog v-model="dialog" max-width="1050px" scrollable>
    <div class="modal-container" color="#333333" dark>

      <v-card-text>
        <v-row>
          <v-col cols="12">
            <ProjectHeroMediaUpload
              :cover-file="selectedFile"
              :banner-file="selectedBannerFile"
              :title="localObra.nome"
              :description="localObra.descricao"
              :metadata="previewMetadata"
              :age-rating="previewAgeRating"
              page-sizing
              @cover-selected="selectedFile = $event"
              @banner-selected="selectedBannerFile = $event"
              @error="showSnackbar($event, 'error')"
            />
          </v-col>
          <v-col cols="12" class="fields-col">
            <v-text-field label="Nome" v-model="localObra.nome" placeholder="Ex: O Nome do Vento"
              :rules="[v => !!v || 'O nome da obra é obrigatório']" outlined dense class="custom-input">
              <template v-slot:append>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on">mdi-information</v-icon>
                  </template>
                  <span>Insira o nome da obra, como o título do livro ou projeto.</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-row>
              <v-col cols="12" sm="6">
                <v-select label="Categoria" v-model="localObra.categoria" :items="categorias" item-text="name"
                  item-value="id" :rules="[v => !!v || 'A categoria é obrigatória']" placeholder="Ex: Livros" outlined
                  dense class="custom-input" :loading="catalogLoading" @change="onCategoryChange">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Selecione a categoria principal da obra, como Livro.</span>
                    </v-tooltip>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select label="Gênero" v-model="localObra.genero" :items="generos" item-text="name" item-value="id"
                  :rules="[v => !!v || 'O gênero é obrigatório']" placeholder="Ex: Romance" outlined dense
                  class="custom-input">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Escolha o gênero, como Romance, Ficção Científica, etc.</span>
                    </v-tooltip>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-select label="Idioma" v-model="localObra.idioma" :items="idiomas" item-text="name" item-value="id"
                  :rules="[v => !!v || 'O idioma é obrigatório']" placeholder="Ex: Português" outlined dense
                  class="custom-input">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Selecione o idioma principal da obra.</span>
                    </v-tooltip>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select label="Tipo da Obra" v-model="localObra.tipo" :items="tiposObra" item-text="name"
                  item-value="id" :rules="[v => !!v || 'O tipo da obra é obrigatório']" placeholder="Ex: Série" outlined
                  dense class="custom-input" :loading="typesLoading" :disabled="typesLoading"
                  no-data-text="Nenhum tipo disponível para esta categoria">
                  <template v-slot:append>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">mdi-information</v-icon>
                      </template>
                      <span>Defina o tipo de obra, como Série, Conto, etc.</span>
                    </v-tooltip>
                  </template>
                </v-select>
                <v-alert v-if="noAvailableTypes" type="warning" dense text class="mt-2" role="alert">
                  {{ categoryGateMessage }}
                </v-alert>
              </v-col>
            </v-row>

            <v-textarea label="Descrição" v-model="localObra.descricao"
              placeholder="Ex: Uma história épica sobre um jovem herói..." outlined dense class="custom-input mt-3">
              <template v-slot:append>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on">mdi-information</v-icon>
                  </template>
                  <span>Descreva brevemente o conteúdo ou objetivo da obra.</span>
                </v-tooltip>
              </template>
            </v-textarea>

            <AgeRatingPickerInline
              v-model="localObra.age_rating_ids"
              :ageRatings="ageRatings"
              class="mt-2"
            />

          </v-col>
        </v-row>
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" top>
          {{ snackbar.message }}
        </v-snackbar>
      </v-card-text>

      <v-card-actions>
        <v-btn fab small color="primary" class="help-button" @click="showHelp" aria-label="Abrir guia de ajuda">
          <v-icon>mdi-help-circle</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="red" @click="closeModal">Cancelar</v-btn>
        <v-btn class="zinnes-primary-btn" @click="saveObra" :loading="loading" :disabled="submissionBlocked">Salvar</v-btn>
      </v-card-actions>
    </div>

    <v-dialog v-model="helpDialog" max-width="500px">
      <v-card>
        <v-card-title>Como preencher o formulário</v-card-title>
        <v-card-text>
          <p><strong>Nome:</strong> Insira o título da sua obra.</p>
          <p><strong>Categoria:</strong> Escolha a categoria principal, como Livro.</p>
          <p><strong>Gênero:</strong> Selecione o gênero, como Romance ou Fantasia.</p>
          <p><strong>Idioma:</strong> Escolha o idioma da obra.</p>
          <p><strong>Tipo da Obra:</strong> Defina o tipo, como Série ou Conto.</p>
          <p><strong>Descrição:</strong> Adicione uma breve descrição da obra.</p>
          <p><strong>Capa da obra:</strong> imagem vertical A5 usada em cards e na capa sobreposta.</p>
          <p><strong>Banner da pagina da obra:</strong> imagem para o topo publico em 1600x920px.</p>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="helpDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import UtilMixins from '@/mixins/UtilMixins.js';
import { getAllProjects, saveWork, getGenders, getProjectType, getLanguages, getCategorys, getAgeRatings } from '../server/workService.js'
import AgeRatingPickerInline from '@/components/AgeRatingPickerInline.vue';
import ProjectHeroMediaUpload from '@/components/ProjectHeroMediaUpload.vue';
import { getHighestAgeRating, normalizeAgeRatingIds } from '@/utils/ageRatings';
import { findCategoryBySlug, isRichText, pickAvailableCategories } from '@/utils/projectCategory';

export default {
  name: "AddNewWorkModal",
  mixins: [UtilMixins],
  components: { AgeRatingPickerInline, ProjectHeroMediaUpload },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    obras: {
      type: Array,
      default: () => []
    },
    categorySlug: {
      type: String,
      default: 'livros'
    }
  },
  data() {
    return {
      snackbar: {
        show: false,
        message: '',
        color: '',
        timeout: 2500,
      },
      dialog: this.value,
      localObra: {
        nome: '',
        categoria: '',
        genero: '',
        idioma: '',
        tipo: '',
        descricao: '',
        age_rating_ids: [],
      },
      idiomas: [],
      tiposObra: [],
      categorias: [],
      generos: [],
      ageRatings: [],
      selectedFile: null,
      selectedBannerFile: null,
      newObras: [],
      helpDialog: false,
      loading: false,
      catalogLoading: false,
      typesLoading: false,
    };
  },

  watch: {
    value(newValue) {
      this.dialog = newValue;
      if (newValue) {
        this.applyCategoryBySlug(this.categorySlug);
      }
    },
    categorySlug() {
      if (this.dialog) {
        this.applyCategoryBySlug(this.categorySlug);
      }
    },
    dialog(newValue) {
      this.$emit('input', newValue);
    },
    newObras(newValue) {
      this.$emit('updateBookNewObra', newValue);
    }
  },

  async created() {
    await this.loadCatalog();
    const genders = await getGenders();
    this.generos = Array.isArray(genders) ? genders : genders?.data || [];
    const languages = await getLanguages();
    this.idiomas = Array.isArray(languages) ? languages : languages?.data || [];
    const ratings = await getAgeRatings();
    this.ageRatings = ratings?.data || ratings || [];
  },

  computed: {
    selectedCategory() {
      return this.categorias.find((category) => category.id === this.localObra.categoria) || null;
    },
    noAvailableTypes() {
      return !!this.selectedCategory && !this.typesLoading && this.tiposObra.length === 0;
    },
    submissionBlocked() {
      return this.catalogLoading || this.typesLoading || this.tiposObra.length === 0;
    },
    categoryGateMessage() {
      return 'Esta categoria ainda não possui um tipo disponível para publicação. Tente novamente mais tarde.';
    },
    previewAgeRating() {
      return getHighestAgeRating(this.ageRatings, this.localObra.age_rating_ids);
    },
    previewMetadata() {
      return [
        this.findName(this.categorias, this.localObra.categoria),
        this.findName(this.tiposObra, this.localObra.tipo),
        this.findName(this.generos, this.localObra.genero),
        this.findName(this.idiomas, this.localObra.idioma),
      ];
    },
  },

  methods: {
    async loadCatalog() {
      this.catalogLoading = true;
      try {
        const catalog = await getCategorys();
        const categories = Array.isArray(catalog) ? catalog : catalog?.data || [];
        this.categorias = pickAvailableCategories(categories).filter(isRichText);
      } catch (error) {
        this.categorias = [];
        this.showSnackbar('Erro ao carregar as categorias.', 'error');
      } finally {
        this.catalogLoading = false;
      }
      this.applyCategoryBySlug(this.categorySlug);
    },
    applyCategoryBySlug(slug) {
      const category = findCategoryBySlug(this.categorias, slug) || this.categorias[0] || null;
      if (!category) {
        this.localObra.categoria = '';
        this.tiposObra = [];
        return;
      }
      this.localObra.categoria = category.id;
      this.loadTypes(category.id);
    },
    async loadTypes(categoryId) {
      this.localObra.tipo = '';
      this.tiposObra = [];
      if (!categoryId) return;
      this.typesLoading = true;
      try {
        const types = await getProjectType({ project_category_id: categoryId });
        this.tiposObra = Array.isArray(types) ? types : types?.data || [];
      } catch (error) {
        this.tiposObra = [];
        this.showSnackbar('Erro ao carregar os tipos da categoria.', 'error');
      } finally {
        this.typesLoading = false;
      }
    },
    onCategoryChange(categoryId) {
      this.loadTypes(categoryId);
    },
    closeModal() {
      this.dialog = false;
      this.localObra = {
        nome: '',
        categoria: '',
        genero: '',
        idioma: '',
        tipo: '',
        descricao: '',
        age_rating_ids: [],
      };
      this.selectedFile = null;
      this.selectedBannerFile = null;
      this.applyCategoryBySlug(this.categorySlug);
    },
    async saveObra() {
      try {
        if (!this.localObra.nome) {
          this.showSnackbar('O campo Nome é obrigatório.', 'error');
          return;
        }
        if (!this.localObra.categoria) {
          this.showSnackbar('Selecione uma Categoria.', 'error');
          return;
        }
        if (!this.localObra.genero) {
          this.showSnackbar('Selecione um Gênero.', 'error');
          return;
        }
        if (!this.localObra.idioma) {
          this.showSnackbar('Selecione um Idioma.', 'error');
          return;
        }
        if (!this.localObra.tipo) {
          this.showSnackbar('Selecione o Tipo da Obra.', 'error');
          return;
        }
        if (!this.selectedFile) {
          this.showSnackbar('Adicione uma imagem para a obra.', 'error');
          return;
        }

        this.loading = true;
        const formData = new FormData();
        formData.append("name", this.localObra.nome);
        formData.append("project_category_id", this.localObra.categoria);
        formData.append("project_type_id", this.localObra.tipo);
        formData.append("gender_id", this.localObra.genero);
        formData.append("language_id", this.localObra.idioma);
        formData.append("description", this.localObra.descricao);
        formData.append("project_thumb", this.selectedFile);
        formData.append("age_rating_ids", JSON.stringify(normalizeAgeRatingIds(this.localObra.age_rating_ids)));
        if (this.isFile(this.selectedBannerFile)) {
          formData.append("project_banner", this.selectedBannerFile);
        }

        await saveWork(formData);
        this.newObras = await getAllProjects();
        this.loading = false;
        this.showSnackbar('Obra salva com sucesso!', 'success');
        this.closeModal();
      } catch (error) {
        this.loading = false;
        this.showSnackbar(`Erro ao salvar a obra: ${error.message}`, 'error');
      }
    },
    showSnackbar(message, type) {
      this.snackbar.message = message;
      this.snackbar.color = type === 'success' ? 'green' : 'red';
      this.snackbar.show = true;
    },
    isFile(value) {
      return Object.prototype.toString.call(value) === '[object File]';
    },
    findName(items, id) {
      const item = items.find((entry) => entry.id === id);
      return item ? item.name : '';
    },
    showHelp() {
      this.helpDialog = true;
    }
  }
};
</script>

<style scoped>
.modal-container {
  background-color: #dadada !important;
  padding: 16px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}

.custom-input {
  margin-bottom: 0;
  color: white !important;
}

.fields-col {
  overflow: visible;
}

.help-button {

  top: 10px;
  right: 10px;
}

@media (max-width: 600px) {
  .modal-container {
    height: auto !important;
    max-height: 90vh;
    overflow-y: auto;
    padding: 8px !important;
    justify-content: flex-start;
  }

  .image-container {
    display: flex;
    justify-content: center;
  }

  .row {
    margin-top: -3px !important;
    margin-bottom: -3px !important;
  }

  .col {
    padding-top: 3px !important;
    padding-bottom: 3px !important;
  }

  ::v-deep .v-messages {
    min-height: 0 !important;
  }

  ::v-deep .v-text-field__details {
    padding-top: 0 !important;
    min-height: 0 !important;
  }
}
</style>
